import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: ('reader' | 'contributor' | 'editor' | 'admin' | 'super_admin')[];
  requireAuthentication?: boolean;
  fallbackPath?: string;
  permission?: keyof ReturnType<typeof useRoleAccess>;
}

export function ProtectedRoute({ 
  children, 
  requiredRoles = [], 
  requireAuthentication = true,
  fallbackPath = '/',
  permission
}: ProtectedRouteProps) {
  const { user, profile, loading } = useAuth();
  const roleAccess = useRoleAccess();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  // Check authentication requirement
  if (requireAuthentication && !user) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // If no profile data available but user exists, redirect to complete profile
  if (requireAuthentication && user && !profile) {
    return <Navigate to="/auth/complete-profile" state={{ from: location }} replace />;
  }

  // Check specific permission if provided
  if (permission && profile) {
    const hasPermission = roleAccess[permission];
    if (typeof hasPermission === 'function' ? !hasPermission() : !hasPermission) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <Alert className="border-orange-200 bg-orange-50">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-800">
                You don't have permission to access this page. Your current role is "{profile.role}".
                {roleAccess.hasMinimumRole('contributor') && (
                  <span className="block mt-2">
                    <button 
                      onClick={() => window.location.href = roleAccess.getDefaultLandingPage()}
                      className="text-orange-600 underline hover:no-underline"
                    >
                      Go to your dashboard
                    </button>
                  </span>
                )}
              </AlertDescription>
            </Alert>
          </div>
        </div>
      );
    }
  }

  // Check role requirements if provided
  if (requiredRoles.length > 0 && profile) {
    const userRole = profile.role as typeof requiredRoles[0];
    const hasRequiredRole = requiredRoles.includes(userRole);
    
    if (!hasRequiredRole) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <Alert className="border-orange-200 bg-orange-50">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-800">
                Access denied. This page requires one of the following roles: {requiredRoles.join(', ')}.
                Your current role is "{profile.role}".
                {roleAccess.hasMinimumRole('contributor') && (
                  <span className="block mt-2">
                    <button 
                      onClick={() => window.location.href = roleAccess.getDefaultLandingPage()}
                      className="text-orange-600 underline hover:no-underline"
                    >
                      Go to your dashboard
                    </button>
                  </span>
                )}
              </AlertDescription>
            </Alert>
          </div>
        </div>
      );
    }
  }

  // All checks passed, render the protected content
  return <>{children}</>;
} 